import { z } from "zod";
import { ProviderId } from "./providers.ts";

/**
 * AgentEvent is the normalized stream event model for both Claude Code
 * (`--output-format stream-json`) and Codex (`codex exec --json`).
 * The CLI worker translates each vendor's raw events into this union before
 * sending anything upstream; web/native clients only ever see AgentEvents.
 */

export const TokenUsage = z.object({
  inputTokens: z.number().optional(),
  outputTokens: z.number().optional(),
  cacheReadTokens: z.number().optional(),
  cacheCreationTokens: z.number().optional(),
  /** Tokens occupying the model's context window at the end of this turn: the
   * final request's input + cache read + cache creation. Per-turn totals above
   * are sums over every request the turn made, so they cannot stand in for it. */
  contextTokens: z.number().optional(),
  /** The selected model's context window size in tokens, when known. */
  contextWindow: z.number().optional(),
  costUsd: z.number().optional(),
});
export type TokenUsage = z.infer<typeof TokenUsage>;

/** A task an agent started and left running — a background shell command or
 * sub-agent — while its own turn carried on or ended. */
export const BackgroundTask = z.object({
  taskId: z.string(),
  description: z.string(),
  /** The provider's own kind ("local_bash", "local_agent"), when stated. */
  kind: z.string().optional(),
  /** The call that started the task, when the provider says. */
  toolUseId: z.string().optional(),
});
export type BackgroundTask = z.infer<typeof BackgroundTask>;

export const AgentEvent = z.discriminatedUnion("type", [
  /** Emitted once per underlying CLI session; used for --resume steering. */
  z.object({ type: z.literal("session.created"), sessionId: z.string(), providerId: ProviderId }),
  z.object({ type: z.literal("turn.started") }),
  /** A queued turn's text, rewritten by its author before the agent picked the
   * turn up. The event's own `turnId` says which user message to rewrite, so
   * clients replace that message's text in place. Deliberately *not* evidence
   * that the turn has started: it is the one agent event a turn can carry while
   * still waiting in the provider's queue. */
  z.object({ type: z.literal("turn.message.revised"), text: z.string() }),
  /** A queued turn its author deleted before the agent picked it up. Clients
   * drop the message from the queue. Like a revision, it is not evidence that
   * the turn has started — it means the turn never will. */
  z.object({ type: z.literal("turn.message.withdrawn") }),
  /** A queued turn its author asked to send now: it jumps the queue and the
   * agent's current turn is interrupted so it can start. Clients move it to the
   * front of the queue; the turn's own first agent event delivers it. */
  z.object({ type: z.literal("turn.message.prioritized") }),
  z.object({ type: z.literal("text.delta"), text: z.string() }),
  /** Complete assistant text block (sent when a block finishes; delta consumers may ignore). */
  z.object({
    type: z.literal("text.done"),
    text: z.string(),
    /** Set when this prose came from a sub-agent rather than from the agent the
     * user is talking to: the id of the tool call that spawned it. Clients nest
     * it inside that call instead of printing it as the agent's own answer. */
    parentToolUseId: z.string().optional(),
  }),
  z.object({ type: z.literal("thinking.delta"), text: z.string() }),
  z.object({
    type: z.literal("tool.started"),
    toolUseId: z.string(),
    name: z.string(),
    input: z.unknown(),
    /** The `toolUseId` of the call that spawned this one — set only for a
     * sub-agent's own tool calls, which ride the parent's stream. Clients nest
     * the call under its spawner rather than showing it as the main agent's. */
    parentToolUseId: z.string().optional(),
  }),
  z.object({
    type: z.literal("tool.completed"),
    toolUseId: z.string(),
    /** Tool name echoed from the matching tool.started, when the provider can
     * pair them; lets consumers label a result without replaying the stream. */
    name: z.string().optional(),
    output: z.string().optional(),
    isError: z.boolean().default(false),
    /** Echoed from the matching tool.started, so a result can be nested under
     * the same parent without replaying the stream. */
    parentToolUseId: z.string().optional(),
  }),
  z.object({
    type: z.literal("turn.completed"),
    usage: TokenUsage.optional(),
    stopReason: z.string().optional(),
  }),
  z.object({ type: z.literal("turn.failed"), error: z.string() }),
  /**
   * Every task the agent has left running in the background, replacing the
   * previous list; empty once the last one has finished. While it is non-empty
   * the turn is not over: the provider wakes itself when a task finishes and
   * carries on in the same turn, so `turn.completed` waits for that.
   */
  z.object({
    type: z.literal("background.tasks"),
    tasks: z.array(BackgroundTask).max(64),
  }),
  /** One background task finished, as the provider summarized it. */
  z.object({
    type: z.literal("background.task.completed"),
    taskId: z.string(),
    /** The call that started the task, when the provider says. */
    toolUseId: z.string().optional(),
    description: z.string().optional(),
    /** The provider's own word: "completed", "failed", "killed", … */
    status: z.string(),
    summary: z.string().optional(),
  }),
  /** Worker-side diagnostics surfaced to the UI (clone progress, CLI missing, …). */
  z.object({
    type: z.literal("log"),
    level: z.enum(["info", "warn", "error"]),
    message: z.string(),
  }),
]);
export type AgentEvent = z.infer<typeof AgentEvent>;

/** The one wording every surface uses for a turn held open by background work.
 * The native apps mirror it (`BackgroundTaskWording`); change both together. */
export function backgroundWaitLine(count: number): string {
  return `Waiting on ${count} background ${count === 1 ? "task" : "tasks"}`;
}

/** Ceiling on one agent event, measured on its JSON serialization in UTF-8.
 * D1 refuses a row over 2 MB, and a single event that never fits wedges a
 * ChatRoom's flush (or, before admission, a worker's whole delivery queue). A
 * quarter of a megabyte is far beyond any transcript a person reads, so
 * truncating past it loses nothing anyone would scroll to. */
export const AGENT_EVENT_MAX_BYTES = 256 * 1024;

/** The string fields `boundAgentEvent` may shorten, in no particular order:
 * it always cuts the largest first. `input` is a tool's arguments, which only
 * become a string once they are what has to be cut. */
const TRUNCATABLE_FIELDS = ["output", "text", "input", "summary", "message", "error"] as const;

const utf8 = new TextEncoder();

function jsonBytes(value: unknown): number {
  return utf8.encode(JSON.stringify(value) ?? "").byteLength;
}

function truncationMarker(droppedBytes: number): string {
  return `\n… [truncated ${Math.max(1, Math.ceil(droppedBytes / 1024))} KB]`;
}

/** `value` cut so its JSON form (marker included) takes at most `budget`
 * bytes. Proportional first, then shrinking: escapes and multi-byte
 * characters make the byte cost of a character uneven. */
function truncateToJsonBytes(value: string, budget: number): string {
  const original = jsonBytes(value);
  let keep = Math.max(0, Math.floor(value.length * (budget / Math.max(original, 1))));
  for (;;) {
    let head = value.slice(0, keep);
    // Never leave half a surrogate pair at the cut.
    const last = head.charCodeAt(head.length - 1);
    if (last >= 0xd800 && last <= 0xdbff) head = head.slice(0, -1);
    const candidate = head + truncationMarker(original - jsonBytes(head));
    if (jsonBytes(candidate) <= budget || keep === 0) return candidate;
    keep = Math.floor(keep * 0.9);
  }
}

/**
 * The event, shortened if need be to fit `AGENT_EVENT_MAX_BYTES`. Pure: the
 * input is never mutated, and an event already within bounds comes back as
 * the same object. The largest truncatable field is cut first and gets a
 * trailing `… [truncated N KB]`, so the reader can tell. An event that still
 * cannot fit (its size lives in fields nothing may shorten) becomes a warning
 * log saying so, which always fits.
 *
 * The worker calls this before batching; the server calls it again at
 * admission, because nothing upstream of the server can be relied on.
 */
export function boundAgentEvent(event: AgentEvent, maxBytes = AGENT_EVENT_MAX_BYTES): AgentEvent {
  let size = jsonBytes(event);
  if (size <= maxBytes) return event;
  const bounded = { ...event } as Record<string, unknown>;
  const candidates = TRUNCATABLE_FIELDS
    .filter((field) => bounded[field] !== undefined && (field === "input" || typeof bounded[field] === "string"))
    .map((field) => ({ field, bytes: jsonBytes(bounded[field]) }))
    .sort((left, right) => right.bytes - left.bytes);
  for (const { field, bytes } of candidates) {
    if (size <= maxBytes) break;
    const current = bounded[field];
    const text = typeof current === "string" ? current : JSON.stringify(current) ?? "";
    // The field's share of the event once the overflow is cut, less the
    // quotes its JSON form adds back.
    const budget = Math.max(0, bytes - (size - maxBytes));
    bounded[field] = truncateToJsonBytes(text, budget);
    size = jsonBytes(bounded);
  }
  if (size <= maxBytes) return bounded as AgentEvent;
  return {
    type: "log",
    level: "warn",
    message: `An agent event (${event.type}) was too large to keep: ${Math.ceil(jsonBytes(event) / 1024)} KB.`,
  };
}
